import { type ComponentPropsWithoutRef } from 'react';

import { Field, type HTMLArkProps, ark } from '@ark-ui/react';

import type { Radius, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type InputProps = Pick<
	ComponentPropsWithoutRef<'input'>,
	| 'placeholder'
	| 'value'
	| 'defaultValue'
	| 'onChange'
	| 'onBlur'
	| 'onFocus'
	| 'name'
	| 'type'
	| 'min'
	| 'max'
	| 'step'
	| 'pattern'
	| 'autoComplete'
	| 'autoFocus'
	| 'id'
>;

type TextFieldRootProps = Omit<
	HTMLArkProps<'div'>,
	'defaultValue' | 'onChange' | 'onBlur' | 'onFocus' | 'id'
> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft' | 'outline';
	radius?: Radius;
	disabled?: boolean;
	invalid?: boolean;
	required?: boolean;
	readOnly?: boolean;
	rootClassName?: string;
	inputClassName?: string;
} & InputProps;

function TextFieldRoot({
	className,
	rootClassName,
	inputClassName,
	size = '2',
	variant = 'surface',
	radius,
	disabled,
	invalid,
	required,
	readOnly,
	children,
	placeholder,
	value,
	defaultValue,
	onChange,
	onBlur,
	onFocus,
	name,
	type,
	min,
	max,
	step,
	pattern,
	autoComplete,
	autoFocus,
	id,
	...props
}: TextFieldRootProps) {
	const hasCustomChildren = children != null;

	const inputProps = {
		placeholder,
		value,
		defaultValue,
		onChange,
		onBlur,
		onFocus,
		name,
		type,
		min,
		max,
		step,
		pattern,
		autoComplete,
		autoFocus,
		id
	};

	const hasInputProps =
		placeholder !== undefined ||
		value !== undefined ||
		onChange !== undefined ||
		name !== undefined;

	return (
		<Field.Root
			disabled={disabled}
			invalid={invalid}
			required={required}
			readOnly={readOnly}
			className={cn('pt-text-field', rootClassName, !hasCustomChildren && className)}
			data-size={size}
			data-variant={variant}
			data-radius={radius}
			{...props}
		>
			{children ?? (hasInputProps && <TextFieldInput className={inputClassName} {...inputProps} />)}
		</Field.Root>
	);
}

function TextFieldLabel({ className, ...props }: HTMLArkProps<'label'>) {
	return <Field.Label className={cn('pt-text-field-label', className)} {...props} />;
}

function TextFieldInput({ className, ...props }: HTMLArkProps<'input'>) {
	return <Field.Input className={cn('pt-text-field-input', className)} {...props} />;
}

type TextFieldSlotProps = HTMLArkProps<'div'> & {
	side?: 'left' | 'right';
};

function TextFieldSlot({ className, side = 'left', ...props }: TextFieldSlotProps) {
	return <ark.div data-side={side} className={cn('pt-text-field-slot', className)} {...props} />;
}

function TextFieldHelper({ className, ...props }: HTMLArkProps<'span'>) {
	return <Field.HelperText className={cn('pt-text-field-helper', className)} {...props} />;
}

function TextFieldError({ className, ...props }: HTMLArkProps<'span'>) {
	return <Field.ErrorText className={cn('pt-text-field-error', className)} {...props} />;
}

const TextField = Object.assign(TextFieldRoot, {
	Root: TextFieldRoot,
	Label: TextFieldLabel,
	Input: TextFieldInput,
	Slot: TextFieldSlot,
	Helper: TextFieldHelper,
	Error: TextFieldError
});

export { TextField, type TextFieldRootProps as TextFieldProps };
